import { useEffect, useRef, useState } from 'react'
import { Check, Trash2 } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { highlightTokensInText, splitByMatches } from '@/shared/utils/smartSearch'

type AbbreviationRowProps = {
	id: number
	abbreviation: string
	expansion: string
	onUpdate: (id: number, fields: { abbreviation?: string; expansion?: string }) => void
	onDelete: (id: number) => void
	searchQuery?: string
	highlight?: boolean
}

function HighlightedText({ text, query }: { text: string; query?: string }) {
	if (!query || !text) return <>{text ?? ''}</>
	const tokens = query.toLowerCase().split(/\s+/).filter(Boolean)
	const ranges = highlightTokensInText(text, tokens)
	const parts = splitByMatches(text, ranges)
	return (
		<>
			{parts.map((part, i) =>
				part.highlighted ? (
					<mark key={i} className="bg-yellow-200/60 dark:bg-yellow-500/30 rounded-sm px-0.5">
						{part.text}
					</mark>
				) : (
					<span key={i}>{part.text}</span>
				),
			)}
		</>
	)
}

export default function AbbreviationRow({
	id,
	abbreviation,
	expansion,
	onUpdate,
	onDelete,
	searchQuery,
	highlight,
}: AbbreviationRowProps) {
	const [editing, setEditing] = useState(false)
	const [editAbbr, setEditAbbr] = useState(abbreviation)
	const [editExpansion, setEditExpansion] = useState(expansion)
	const [saveError, setSaveError] = useState<string | null>(null)
	const abbrInputRef = useRef<HTMLInputElement>(null)
	const rowRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (editing && abbrInputRef.current) {
			abbrInputRef.current.focus()
			abbrInputRef.current.select()
		}
	}, [editing])

	const startEditing = () => {
		setEditAbbr(abbreviation)
		setEditExpansion(expansion)
		setSaveError(null)
		setEditing(true)
	}

	const save = () => {
		const trimmedAbbr = editAbbr.trim()
		const trimmedExp = editExpansion.trim()

		if (!trimmedAbbr || trimmedAbbr.length < 2 || trimmedAbbr.length > 50 || /[^a-zA-Z0-9]/.test(trimmedAbbr)) {
			setSaveError('Abbreviation must be 2\u201350 letters or numbers')
			return
		}
		if (!/[a-zA-Z]/.test(trimmedAbbr)) {
			setSaveError('Abbreviation must include at least one letter')
			return
		}
		if (!trimmedExp || trimmedExp.length > 500) {
			setSaveError('Expansion is required and must be 500 characters or less')
			return
		}

		setSaveError(null)
		const abbrChanged = trimmedAbbr !== abbreviation
		const expChanged = trimmedExp !== expansion
		if (abbrChanged || expChanged) {
			const fields: { abbreviation?: string; expansion?: string } = {}
			if (abbrChanged) fields.abbreviation = trimmedAbbr
			if (expChanged) fields.expansion = trimmedExp
			onUpdate(id, fields)
		}
		setEditing(false)
	}

	const handleBlur = (e: React.FocusEvent) => {
		// If focus is moving to another element within this row, don't save yet
		const related = e.relatedTarget as HTMLElement | null
		if (related && rowRef.current?.contains(related)) return
		save()
	}

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') save()
		if (e.key === 'Escape') cancel()
	}

	const cancel = () => {
		setEditAbbr(abbreviation)
		setEditExpansion(expansion)
		setSaveError(null)
		setEditing(false)
	}

	const handleRowClick = (e: React.MouseEvent) => {
		if (editing) return
		// Don't trigger if clicking a button (delete)
		const target = e.target as HTMLElement
		if (target.closest('button[data-no-blur-save]')) return
		startEditing()
	}

	return (
		<div
			ref={rowRef}
			onClick={handleRowClick}
			className={`group flex items-center gap-3 rounded-md px-3 py-2 transition-colors ${editing ? 'bg-muted/50' : 'hover:bg-muted/50 cursor-pointer'} ${highlight ? 'animate-highlight-fade' : ''}`}
		>
			<div className="shrink-0">
				{editing ? (
					<Input
						ref={abbrInputRef}
						value={editAbbr}
						onChange={(e) => setEditAbbr(e.target.value.replace(/[^a-zA-Z0-9]/g, ''))}
						onKeyDown={handleKeyDown}
						onBlur={handleBlur}
						maxLength={50}
						className="h-8 w-28 font-mono text-sm"
					/>
				) : (
					<span className="font-mono text-sm font-semibold text-primary bg-secondary/60 rounded px-2 py-0.5 min-w-[60px] inline-block">
						<HighlightedText text={abbreviation} query={searchQuery} />
					</span>
				)}
			</div>

			<div className="flex-1 min-w-0">
				{editing ? (
					<div className="flex flex-col gap-1">
						<Input
							value={editExpansion}
							onChange={(e) => setEditExpansion(e.target.value)}
							onKeyDown={handleKeyDown}
							onBlur={handleBlur}
							maxLength={500}
							className="h-8 text-sm"
						/>
						{saveError && (
							<span className="text-[11px] text-destructive">
								{saveError}
							</span>
						)}
						{editExpansion.length > 400 && (
							<span className="text-[11px] text-muted-foreground text-right">
								{editExpansion.length}/500
							</span>
						)}
					</div>
				) : (
					<span className="text-sm text-foreground break-words block">
						<HighlightedText text={expansion} query={searchQuery} />
					</span>
				)}
			</div>

			{editing ? (
				<Button
					variant="ghost"
					size="icon"
					onClick={save}
					className="h-7 w-7 text-muted-foreground hover:text-green-600 shrink-0"
					aria-label="Save changes"
					title="Save"
				>
					<Check className="h-3.5 w-3.5" />
				</Button>
			) : (
				<Button
					variant="ghost"
					size="icon"
					onClick={() => onDelete(id)}
					data-no-blur-save
					className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
					aria-label={`Delete abbreviation ${abbreviation}`}
					title="Delete"
				>
					<Trash2 className="h-3.5 w-3.5" />
				</Button>
			)}
		</div>
	)
}
